import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCompany, useCreateCompany, useInvitations, useCreateInvitations } from "@/hooks";
import { UtilsService } from "@/services/utils/utils.service";
import { Button } from "@/components/ui/button";
import { StyledInput } from "@/components/ui/styled-input";
import { StyledLabel } from "@/components/ui/styled-label";
import { StyledTextarea } from "@/components/ui/styled-textarea";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { X } from "lucide-react";
import type { CompanyInvitation } from "@/types/user.types";

const utils = new UtilsService();

const companySchema = z.object({
  name: z.string().min(1, "Company name is required").min(2, "Company name must be at least 2 characters"),
  address: z.string().optional(),
  website: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  phone: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyDetailsStepProps {
  onComplete: () => void;
  isCreatingNew?: boolean;
}

export const CompanyDetailsStep = ({ onComplete, isCreatingNew = false }: CompanyDetailsStepProps) => {
  const { t } = useTranslation();
  const { data: company } = useCompany();
  const createCompanyMutation = useCreateCompany();
  const { data: invitationsData } = useInvitations();
  const invitations: CompanyInvitation[] = (invitationsData as CompanyInvitation[] | undefined) || [];
  const createInvitationsMutation = useCreateInvitations();

  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");

  // Only use company data if NOT creating a new company
  const shouldUseCompanyData = !isCreatingNew && company;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: shouldUseCompanyData ? (company?.name || "") : "",
      address: shouldUseCompanyData ? (company?.address || "") : "",
      website: shouldUseCompanyData ? (company?.website || "") : "",
      phone: shouldUseCompanyData ? (company?.phone || "") : "",
    },
  });

  const phoneValue = watch("phone");

  useEffect(() => {
    // Only update form values with company data if NOT creating new
    if (shouldUseCompanyData) {
      setValue("name", company.name || "");
      setValue("address", company.address || "");
      setValue("website", company.website || "");
      setValue("phone", company.phone || "");
    }
  }, [company, setValue, shouldUseCompanyData]);

  const addEmail = () => {
    const email = emailInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !inviteEmails.includes(email)) {
      setInviteEmails([...inviteEmails, email]);
      setEmailInput("");
    }
  };

  const removeEmail = (email: string) => {
    setInviteEmails(inviteEmails.filter((e) => e !== email));
  };

  const onSubmit = async (data: CompanyFormData) => {
    try {
      let companyCreated = false;

      // Create company if creating new OR if it doesn't exist
      if (isCreatingNew || !company?.id) {
        const createdCompany = await createCompanyMutation.mutateAsync(data);

        // UtilsService may return an error payload instead of throwing
        if (!createdCompany || !(createdCompany as any).id) {
          const errorMessage =
            utils.extractErrorMessage(
              createdCompany,
              t("onboarding.companyCreationFailed")
            ) ||
            t("onboarding.companyCreationFailed") ||
            "Failed to create company. Please try again.";

          toast.error(errorMessage);
          // Stay on current step - don't call onComplete
          return;
        }

        companyCreated = true;
      }

      // Store invitation emails in localStorage to send after payment completion
      if (inviteEmails.length > 0) {
        const validEmails = inviteEmails.filter((email) => email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
        if (validEmails.length > 0) {
          localStorage.setItem('pendingInvitationEmails', JSON.stringify(validEmails));
        }
      }

      // Show success message only when we actually created a new company
      if (companyCreated) {
        if (inviteEmails.length > 0) {
          toast.success(t("onboarding.companyCreatedWithPendingInvitations") || "Company created");
        } else {
          toast.success(t("onboarding.companyCreated"));
        }
      }

      // Only call onComplete if everything succeeded
      onComplete();
    } catch (error: unknown) {
      const errorMessage = utils.extractErrorMessage(
        error,
        t("onboarding.companyCreationFailed")
      );
      toast.error(errorMessage);
      // Do NOT call onComplete() - stay on current step
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-2">{t("onboarding.steps.company.title")}</h2>
      <p className="text-gray-600 mb-6">{t("onboarding.steps.company.description")}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <StyledLabel htmlFor="name" required>
            {t("onboarding.steps.company.name")}
          </StyledLabel>
          <StyledInput
            id="name"
            type="text"
            placeholder={t("onboarding.steps.company.name")}
            error={errors.name?.message}
            {...register("name")}
          />
        </div>

        <div>
          <StyledLabel htmlFor="address">
            {t("onboarding.steps.company.address")}
          </StyledLabel>
          <StyledTextarea
            id="address"
            {...register("address")}
            rows={3}
            placeholder={t("onboarding.steps.company.address")}
            error={errors.address?.message}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <StyledLabel htmlFor="website">
              {t("onboarding.steps.company.website")}
            </StyledLabel>
            <StyledInput
              id="website"
              type="url"
              placeholder="https://example.com"
              error={errors.website?.message}
              {...register("website")}
            />
          </div>

          <div>
            <StyledLabel htmlFor="phone">
              {t("onboarding.steps.company.phone")}
            </StyledLabel>
            <div className="mt-1">
              <PhoneInput
                country={'us'}
                value={phoneValue || ""}
                onChange={(value) => {
                  setValue("phone", value, { shouldValidate: true });
                }}
                inputProps={{
                  id: "phone",
                }}
                containerClass={errors.phone ? "phone-input-error" : ""}
                inputClass={errors.phone ? "border-red-500" : ""}
              />
            </div>
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1 pl-2">{errors.phone.message}</p>
            )}
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">{t("onboarding.steps.company.inviteTeam")}</h3>
          <p className="text-sm text-gray-600 mb-4">{t("onboarding.steps.company.inviteDescription")}</p>

          <div className="flex gap-2 mb-4">
            <StyledInput
              type="email"
              placeholder={t("onboarding.steps.company.emailPlaceholder")}
              containerStyles="!rounded flex-1"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addEmail();
                }
              }}
            />
            <Button type="button" onClick={addEmail} variant="outline">
              {t("onboarding.steps.company.addEmail")}
            </Button>
          </div>

          {inviteEmails.length > 0 && (
            <div className="space-y-2 mb-4">
              {inviteEmails.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded"
                >
                  <span className="text-sm">{email}</span>
                  <button
                    type="button"
                    onClick={() => removeEmail(email)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {invitations.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">{t("onboarding.steps.company.pendingInvitations")}</p>
              <div className="space-y-2">
                {invitations.map((invitation: CompanyInvitation) => (
                  <div
                    key={invitation.id}
                    className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded"
                  >
                    <span className="text-sm">{invitation.email}</span>
                    <span className="text-xs text-blue-600">{t("onboarding.steps.company.pending")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button
            type="submit"
            disabled={createCompanyMutation.isPending || createInvitationsMutation.isPending}
          >
            {(createCompanyMutation.isPending || createInvitationsMutation.isPending)
              ? t("common.saving")
              : t("onboarding.steps.company.continue")}
          </Button>
        </div>
      </form>
    </div>
  );
};

